/*
  Rebuilds the "articles tagged X" grids from the Storefront API.

  Liquid caps blogs[handle].articles at the 50 most recent articles per blog, so any
  server-rendered grid silently drops older tagged articles once a blog grows past that.
  This script re-renders every [data-tagged-articles] container from the Storefront API,
  which has no such cap. The API searches every blog at once, so a grid that is meant to
  cover only certain blogs must say so with data-blogs - otherwise it shows more than the
  Liquid fallback it replaces. The article the visitor is currently reading is always
  dropped, so a related-posts grid never links back to itself.

  It is progressive enhancement on purpose: the Liquid markup is left alone until a fetch
  succeeds, so no JS, a failed request or an empty result all keep the server-rendered
  grid instead of blanking it.

  Container contract:
    data-tagged-articles  required, the tag to search for
    data-blogs            comma separated blog handles to keep (default: every blog)
    data-card-class       card wrapper class      (default "article-item")
    data-card-date        if present, adds data-date="<published at>" to each card
    data-image-width      CDN width for the image (default 600)
    data-empty-message    full text shown when the grid ends up with no cards.
                          Default: a line built from the page name (see emptyMessage).
                          Set it to "" to leave an empty grid empty.
    data-empty-name       overrides the name used in that default line
*/
(function () {
  if (window.taggedArticlesLoaded) return;
  window.taggedArticlesLoaded = true;

  var API_VERSION = '2024-10';
  // Public Storefront API token - read-only and safe to expose. Rotate here if it changes.
  var TOKEN = 'ec1f30268ea358d8759efa3200a0ff8f';

  var QUERY =
    'query($q:String!,$after:String){' +
    '  articles(first:250, after:$after, sortKey:PUBLISHED_AT, reverse:true, query:$q){' +
    '    pageInfo{ hasNextPage }' +
    '    edges{ cursor node{ title handle publishedAt blog{ handle } image{ url altText } } }' +
    '  }' +
    '}';

  function shopDomain() {
    return window.taggedArticlesShop || (window.Shopify && window.Shopify.shop);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function sized(url, px) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + px;
  }

  // Paginates in case tagged articles ever go past 250.
  function fetchByTag(tag) {
    var out = [];
    // Quotes wrap the tag so values with spaces ("Elliot Brown") match as one term.
    var q = "tag:'" + String(tag).replace(/'/g, "\\'") + "'";

    function page(after) {
      return fetch('https://' + shopDomain() + '/api/' + API_VERSION + '/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': TOKEN },
        body: JSON.stringify({ query: QUERY, variables: { q: q, after: after || null } })
      }).then(function (r) { return r.json(); }).then(function (j) {
        var conn = j && j.data && j.data.articles;
        if (!conn) throw new Error('Unexpected Storefront API response');
        conn.edges.forEach(function (e) { out.push(e.node); });
        if (conn.pageInfo.hasNextPage && conn.edges.length) {
          return page(conn.edges[conn.edges.length - 1].cursor);
        }
        return out;
      });
    }
    return page(null);
  }

  function path(post) {
    return '/blogs/' + (post.blog ? post.blog.handle : '') + '/' + post.handle;
  }

  // True for the article being read right now. The path may carry a locale prefix
  // (/en/blogs/...), so match the tail rather than the whole pathname.
  function isCurrent(post) {
    var here = String(location.pathname).replace(/\/+$/, '');
    return here === path(post) || here.slice(-path(post).length) === path(post);
  }

  function card(post, opts) {
    var url = esc(path(post));
    var img = post.image ? sized(post.image.url, opts.width) : '';
    var alt = esc(post.image && post.image.altText ? post.image.altText : post.title);
    var date = opts.withDate ? ' data-date="' + esc(post.publishedAt) + '"' : '';

    return '<div class="' + esc(opts.cardClass) + '"' + date + '>' +
      '<div class="cate-all-blog-main">' +
      '<a href="' + url + '">' +
      (img ? '<img src="' + esc(img) + '" alt="' + alt + '" loading="lazy">' : '') +
      '<h2 class="cont_article_title">' + esc(post.title) + '</h2>' +
      '</a></div></div>';
  }


  var GENERIC_EMPTY = 'Stories are on the way — check back soon.';

  // The page name, so the empty state reads like the page it sits on rather than a
  // generic notice. Nearly every template carrying a grid opens with a .page_title
  // heading; the <title> tag is the fallback for the one that does not.
  function pageName(grid) {
    var explicit = grid.getAttribute('data-empty-name');
    if (explicit) return explicit.trim();

    var heading = document.querySelector('.page_title');
    if (heading && heading.textContent.trim()) return heading.textContent.trim();

    // "Ariel Adams – WatchDNA" -> "Ariel Adams"
    var title = String(document.title || '').split(/\s[–—|]\s/)[0].trim();
    return title || '';
  }

  // "Ariel Adams" -> "Ariel Adams's", "Thomas Rogers" -> "Thomas Rogers'"
  function possessive(name) {
    return name + (/s$/i.test(name) ? '’' : '’s');
  }

  function emptyMessage(grid) {
    var name = pageName(grid);
    if (!name) return GENERIC_EMPTY;
    return possessive(name) + ' stories are on the way — check back soon.';
  }

  // A heading followed by a blank grid reads as broken, so a grid that genuinely has
  // nothing to show says so. Only ever fills a grid that has no element children, so it
  // can never overwrite the Liquid fallback.
  function showEmpty(grid) {
    if (grid.querySelector('*')) return;
    if (grid.hasAttribute('data-empty-message') && !grid.getAttribute('data-empty-message')) return;
    var message = grid.getAttribute('data-empty-message') || emptyMessage(grid);

    var p = document.createElement('p');
    p.className = 'tagged-articles-empty';
    p.textContent = message;
    grid.appendChild(p);
  }

  function render(grid) {
    var tag = grid.getAttribute('data-tagged-articles');
    if (!tag || !shopDomain()) return showEmpty(grid);

    var opts = {
      cardClass: grid.getAttribute('data-card-class') || 'article-item',
      withDate: grid.hasAttribute('data-card-date'),
      width: grid.getAttribute('data-image-width') || 600
    };

    var allowed = (grid.getAttribute('data-blogs') || '')
      .split(',').map(function (h) { return h.trim(); }).filter(Boolean);

    fetchByTag(tag).then(function (posts) {
      posts = posts.filter(function (p) {
        if (isCurrent(p)) return false;
        if (!allowed.length) return true;
        return allowed.indexOf(p.blog ? p.blog.handle : '') !== -1;
      });
      // An empty result is more likely a bad tag than a genuinely empty grid,
      // so leave whatever Liquid rendered rather than wiping it.
      if (!posts.length) return showEmpty(grid);
      grid.innerHTML = posts.map(function (p) { return card(p, opts); }).join('');
    }).catch(function (err) {
      console.error('Tagged articles (' + tag + '):', err);
      showEmpty(grid);
    });
  }

  function init() {
    var grids = document.querySelectorAll('[data-tagged-articles]');
    Array.prototype.forEach.call(grids, render);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
